/* EILE in the product (Ale 2026-08-30): a draggable launcher blob + a
 * right slide-over panel — the assistant lives beside the work, never on
 * top of it (the v2 shell gains a right margin while the panel is open,
 * so text reflows instead of being covered).
 *
 * The panel iframes the harness chat app in embed mode. Product and
 * harness share the origin (nginx path routing) and the same
 * localStorage token key, so the officer is already logged in inside
 * the panel and their conversations follow them between the full page
 * and the panel.
 *
 * Clicking the blob offers three entries, each with its own behavior:
 *   - ask about THIS screen  → DOM snapshot of the current view, attached
 *   - ask about an area      → drag-select a region, cropped and attached
 *   - free question          → plain chat
 * Capture is dependency-free: the document is serialized into an SVG
 * foreignObject and rasterized. Fonts may fall back in the shot; that is
 * fine — the assistant reads layout and text, not typography.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { NolosMark } from '../design/Logo'

const POS_KEY = 'nolos-eile-blob-pos'
const PANEL_W = 'min(430px, 92vw)'
const HELP_URL = '/harness/help?embed=1'

type Pos = { x: number; y: number }

function loadPos(): Pos | null {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY) || '')
    return typeof p?.x === 'number' && typeof p?.y === 'number' ? p : null
  } catch {
    return null
  }
}

function capturePage(cb: (canvas: HTMLCanvasElement | null, scale: number) => void) {
  try {
    const w = window.innerWidth
    const h = window.innerHeight
    const clone = document.documentElement.cloneNode(true) as HTMLElement
    clone
      .querySelectorAll('[data-eile-chrome], script, iframe, link[rel="stylesheet"]')
      .forEach((n) => n.remove())
    clone.style.background = getComputedStyle(document.body).backgroundColor || '#f7f8fa'
    // The app's CSS lives in external files, which an <img>-loaded SVG cannot
    // fetch — inline every same-origin rule so the capture is styled.
    let cssText = ''
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules)) cssText += rule.cssText + '\n'
      } catch {
        /* cross-origin sheet (fonts CDN) — skip; text renders in fallback */
      }
    }
    const styleEl = document.createElement('style')
    styleEl.textContent = cssText
    ;(clone.querySelector('head') || clone).prepend(styleEl)
    const xml = new XMLSerializer().serializeToString(clone)
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">` +
      `<foreignObject width="100%" height="100%" x="0" y="${-window.scrollY}">${xml}</foreignObject></svg>`
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(window.devicePixelRatio || 1, 2)
      const c = document.createElement('canvas')
      c.width = w * scale
      c.height = h * scale
      const ctx = c.getContext('2d')
      if (!ctx) return cb(null, 1)
      ctx.scale(scale, scale)
      ctx.drawImage(img, 0, 0)
      cb(c, scale)
    }
    img.onerror = () => cb(null, 1)
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
  } catch {
    cb(null, 1)
  }
}

export default function EileAssistant() {
  const [open, setOpen] = useState(false)
  const [chips, setChips] = useState(false)
  const [selecting, setSelecting] = useState(false)
  const [pos, setPos] = useState<Pos | null>(loadPos)
  const [frameOn, setFrameOn] = useState(false)
  const blobRef = useRef<HTMLButtonElement>(null)
  const frameRef = useRef<HTMLIFrameElement>(null)
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(
    null
  )
  const selStart = useRef<Pos | null>(null)
  const [selBox, setSelBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null)

  // Reflow the shell instead of covering it.
  useEffect(() => {
    const shell = document.querySelector<HTMLElement>('.v2')
    if (!shell) return
    shell.style.transition = 'margin-right .22s ease'
    shell.style.marginRight = open ? PANEL_W : ''
    return () => {
      shell.style.marginRight = ''
    }
  }, [open])

  const doOpen = useCallback(() => {
    setFrameOn(true)
    setOpen(true)
    setChips(false)
  }, [])
  const doClose = useCallback(() => setOpen(false), [])

  // On first open the iframe does not exist yet (it renders with the panel),
  // so a shot is parked here and posted from the iframe's own onLoad.
  const pendingShot = useRef<string | null>(null)
  const postShot = useCallback((dataUrl: string) => {
    frameRef.current?.contentWindow?.postMessage({ eile: 'attach', dataUrl }, '*')
  }, [])
  const sendShot = useCallback(
    (canvas: HTMLCanvasElement) => {
      const dataUrl = canvas.toDataURL('image/png')
      doOpen()
      const f = frameRef.current
      if (f && f.contentDocument?.readyState === 'complete' && f.src) {
        setTimeout(() => postShot(dataUrl), 150)
      } else {
        pendingShot.current = dataUrl
      }
    },
    [doOpen, postShot]
  )

  const askScreen = useCallback(() => {
    setChips(false)
    capturePage((c) => (c ? sendShot(c) : doOpen()))
  }, [sendShot, doOpen])

  const askArea = useCallback(() => {
    setChips(false)
    setSelecting(true)
  }, [])

  // Esc closes (also forwarded from inside the iframe); selection first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (selecting) {
        setSelecting(false)
        setSelBox(null)
      } else if (chips) setChips(false)
      else doClose()
    }
    const onMsg = (e: MessageEvent) => {
      if (e.data?.eile === 'close') doClose()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('message', onMsg)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('message', onMsg)
    }
  }, [selecting, chips, doClose])

  const blobStyle: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y }
    : { right: 26, bottom: 26 }

  return (
    <>
      {!open && (
        <button
          ref={blobRef}
          data-eile-chrome
          title="EILE"
          style={{
            position: 'fixed',
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            background: 'var(--brand-navy)',
            color: '#fff',
            border: 0,
            borderRadius: 99,
            padding: '9px 15px 9px 11px',
            font: '700 var(--text-12) var(--font-mono, monospace)',
            letterSpacing: '.1em',
            cursor: 'grab',
            boxShadow: '0 3px 14px rgba(4,26,84,.35)',
            userSelect: 'none',
            touchAction: 'none',
            ...blobStyle,
          }}
          onPointerDown={(e) => {
            const r = e.currentTarget.getBoundingClientRect()
            drag.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, moved: false }
            e.currentTarget.setPointerCapture(e.pointerId)
          }}
          onPointerMove={(e) => {
            const d = drag.current
            if (!d) return
            const dx = e.clientX - d.sx
            const dy = e.clientY - d.sy
            if (Math.abs(dx) + Math.abs(dy) > 6) d.moved = true
            if (d.moved) {
              const el = e.currentTarget
              const x = Math.max(6, Math.min(d.ox + dx, window.innerWidth - el.offsetWidth - 6))
              const y = Math.max(6, Math.min(d.oy + dy, window.innerHeight - el.offsetHeight - 6))
              setPos({ x, y })
            }
          }}
          onPointerUp={() => {
            const d = drag.current
            drag.current = null
            if (d?.moved) {
              setPos((p) => {
                if (p)
                  try {
                    localStorage.setItem(POS_KEY, JSON.stringify(p))
                  } catch {
                    /* per-viewer nicety */
                  }
                return p
              })
            } else setChips((c) => !c)
          }}
        >
          <NolosMark size={18} />
          <span>EILE</span>
        </button>
      )}

      {chips && !open && (
        <div
          data-eile-chrome
          style={{
            position: 'fixed',
            zIndex: 61,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            background: '#fff',
            border: '1px solid var(--brand-line)',
            borderRadius: 12,
            padding: 8,
            boxShadow: '0 6px 24px rgba(4,26,84,.18)',
            ...(pos
              ? { left: Math.min(pos.x, window.innerWidth - 280), top: Math.max(8, pos.y - 140) }
              : { right: 26, bottom: 72 }),
          }}
        >
          {/* Three text chips with mono glyphs — no emoji, no canned
              question (audit cut 12, 2026-09-04). */}
          {[
            { icon: '▣', label: 'Ask about this screen', act: askScreen },
            { icon: '▭', label: 'Ask about an area (you choose)', act: askArea },
            { icon: '›', label: 'Free question', act: doOpen },
          ].map((c) => (
            <button
              key={c.label}
              onClick={c.act}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: 0,
                background: 'none',
                color: 'var(--brand-ink)',
                borderRadius: 8,
                padding: '8px 10px',
                font: '500 var(--text-14) var(--font-body, sans-serif)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--brand-line-soft)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <b
                style={{ fontSize: 'var(--text-14)', color: 'var(--brand-muted)' }}
                aria-hidden="true"
              >
                {c.icon}
              </b>{' '}
              {c.label}
            </button>
          ))}
        </div>
      )}

      {selecting && (
        <div
          data-eile-chrome
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 62,
            cursor: 'crosshair',
            background: 'rgba(4,26,84,.18)',
          }}
          onPointerDown={(e) => {
            selStart.current = { x: e.clientX, y: e.clientY }
            setSelBox({ x: e.clientX, y: e.clientY, w: 0, h: 0 })
          }}
          onPointerMove={(e) => {
            const s = selStart.current
            if (!s) return
            setSelBox({
              x: Math.min(s.x, e.clientX),
              y: Math.min(s.y, e.clientY),
              w: Math.abs(e.clientX - s.x),
              h: Math.abs(e.clientY - s.y),
            })
          }}
          onPointerUp={() => {
            const box = selBox
            selStart.current = null
            setSelecting(false)
            setSelBox(null)
            if (!box || box.w < 12 || box.h < 12) return
            capturePage((c, scale) => {
              if (!c) return doOpen()
              const crop = document.createElement('canvas')
              crop.width = box.w * scale
              crop.height = box.h * scale
              crop
                .getContext('2d')
                ?.drawImage(
                  c,
                  box.x * scale,
                  box.y * scale,
                  box.w * scale,
                  box.h * scale,
                  0,
                  0,
                  box.w * scale,
                  box.h * scale
                )
              sendShot(crop)
            })
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 14,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--brand-navy)',
              color: '#fff',
              borderRadius: 99,
              padding: '6px 14px',
              font: '600 var(--text-14) var(--font-body, sans-serif)',
            }}
          >
            Drag to choose the area · Esc to cancel
          </div>
          {selBox && (
            <div
              style={{
                position: 'absolute',
                left: selBox.x,
                top: selBox.y,
                width: selBox.w,
                height: selBox.h,
                border: '2px solid var(--brand-cyan)',
                background: 'rgba(24,198,212,.12)',
              }}
            />
          )}
        </div>
      )}

      <aside
        aria-label="EILE"
        inert={!open}
        data-eile-chrome
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: PANEL_W,
          zIndex: 59,
          background: '#fff',
          borderLeft: '1px solid var(--brand-line)',
          boxShadow: '-6px 0 24px rgba(4,26,84,.12)',
          transform: open ? 'none' : 'translateX(102%)',
          transition: 'transform .22s ease',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            background: 'var(--brand-navy)',
            color: '#fff',
            font: '700 var(--text-12) var(--font-mono, monospace)',
            letterSpacing: '.12em',
          }}
        >
          <NolosMark size={18} />
          <span>EILE</span>
          <span style={{ flex: 1 }} />
          <a
            href="/harness/help"
            target="_blank"
            rel="noreferrer"
            title="Open full page"
            style={{ color: 'var(--brand-rail-text)', textDecoration: 'none', padding: '2px 6px' }}
          >
            ⧉
          </a>
          <button
            onClick={doClose}
            title="Close"
            style={{
              border: 0,
              background: 'none',
              color: 'var(--brand-rail-text)',
              cursor: 'pointer',
              padding: '2px 6px',
            }}
          >
            ✕
          </button>
        </div>
        {frameOn && (
          <iframe
            ref={frameRef}
            src={HELP_URL}
            title="EILE"
            style={{ flex: 1, border: 0, width: '100%' }}
            onLoad={() => {
              const shot = pendingShot.current
              if (shot) {
                pendingShot.current = null
                setTimeout(() => postShot(shot), 300)
              }
            }}
          />
        )}
      </aside>
    </>
  )
}
